import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { actionLabel, conditionLabel, protocolNames } from '@/lib/conversation-model';
import { thresholdLabel } from '@/lib/protocol-labels';
import type { PlayedCascade } from '@matter_extractor/shared';
import { objectiveCriterionGroups, plancherBound } from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { useCallback } from 'react';

/**
 * « Le protocole joué », in reading (§12.3).
 *
 * ## The FROZEN content, and nothing fetched
 *
 * `cascade.protocol.content` is « le protocole ENTIER, tel qu'il a été figé »
 * (§12.2 contract) and it travels with the session precisely so this drawer
 * exists without a request: a frozen version is reached through the session,
 * never through the protocol's own history, so there is no address to fetch it
 * from. What the footer offers instead are the `?version=` links §12.3 asks for -
 * the editors, opened READ-ONLY on the versions this session played.
 *
 * ## A drawer, at the régime of `history-drawer`
 *
 * A native `<dialog open>` kept in flow by React state - non-modal, so it makes
 * nothing inert - with Escape wired by hand and the focus landing on the close
 * button, which is the shortest way back out of a surface whose whole content is
 * reading matter.
 */

export interface PlayedProtocolDrawerProps {
  cascade: PlayedCascade;
  onClose: () => void;
}

export function PlayedProtocolDrawer({ cascade, onClose }: PlayedProtocolDrawerProps) {
  const focusOnMount = useCallback((node: HTMLButtonElement | null) => node?.focus(), []);
  const protocol = cascade.protocol;
  const names = protocolNames(protocol);

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-[var(--backdrop)]">
      {/* The veil is a button for the reason `history-drawer` gives: a clickable
          region has to be reachable and named. */}
      <button
        type="button"
        aria-label="Fermer le tiroir"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />
      <dialog
        open
        aria-label="Le protocole joué"
        data-testid={SESSIONS_TEST_IDS.playedProtocol}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
        className="relative m-0 flex h-full w-full max-w-160 flex-col gap-4 overflow-y-auto border-gray-200 border-l-2 bg-white p-6 text-gray-800"
      >
        <button
          ref={focusOnMount}
          type="button"
          aria-label="Fermer"
          onClick={onClose}
          className="absolute top-3 right-3 inline-flex size-9 cursor-pointer items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X aria-hidden="true" className="size-5" />
        </button>

        {protocol === null ? (
          <p className="text-gray-700">
            La version de protocole que cette session a jouée n’est plus lisible. La conversation
            reste rejouable : les tours portent leur propre instantané (§8.7).
          </p>
        ) : (
          <>
            <header className="flex flex-col gap-1 pr-10">
              <h2 className="font-bold text-bleu-nuit text-xl">{protocol.name}</h2>
              <p className="text-gray-600 text-sm">
                version jouée v{protocol.versionNumber} · {protocol.content.steps.length} étape
                {protocol.content.steps.length > 1 ? 's' : ''}
              </p>
              {protocol.content.description === '' ? null : (
                <p className="text-gray-700 text-sm">{protocol.content.description}</p>
              )}
            </header>

            {protocol.content.steps.map((step, rank) => (
              <section
                key={step.id}
                data-testid={SESSIONS_TEST_IDS.playedStep}
                data-step-id={step.id}
                className="flex flex-col gap-2 rounded-md border border-gray-200 p-4"
              >
                <h3 className="font-semibold text-base text-noir">
                  Étape {rank + 1} · {step.name}
                </h3>

                {step.direction === '' ? null : (
                  <p className="whitespace-pre-wrap break-words text-gray-700 text-sm">
                    {step.direction}
                  </p>
                )}

                <p className="text-gray-600 text-xs">
                  Règles générales : plafond{' '}
                  {step.rules.maxTurns === null ? 'non posé' : `${step.rules.maxTurns} tours`} ·{' '}
                  {step.rules.attempts} essai{step.rules.attempts > 1 ? 's' : ''} ·{' '}
                  {step.rules.emptyAnswersAllowed} réponse
                  {step.rules.emptyAnswersAllowed > 1 ? 's' : ''} vide
                  {step.rules.emptyAnswersAllowed > 1 ? 's' : ''} autorisée
                  {step.rules.emptyAnswersAllowed > 1 ? 's' : ''} · repli :{' '}
                  {step.rules.fallback.kind === 'go-to-step'
                    ? `aller à l’étape ${names.step(step.rules.fallback.stepId)}`
                    : 'fermer l’expérience'}
                </p>

                {step.objectives.length === 0 ? (
                  <p className="text-gray-500 text-sm">Aucun objectif.</p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {step.objectives.map((objective) => (
                      <li key={objective.id} className="border-gray-200 border-l-2 pl-3">
                        <p className="font-semibold text-gray-900 text-sm">
                          {objective.name}{' '}
                          <span className="font-normal text-gray-500 text-xs">
                            {objective.mandatory ? 'obligatoire' : 'optionnel'}
                          </span>
                        </p>
                        {objective.direction === '' ? null : (
                          <p className="whitespace-pre-wrap break-words text-gray-700 text-sm">
                            {objective.direction}
                          </p>
                        )}
                        <ul className="text-gray-600 text-xs">
                          {objectiveCriterionGroups(objective).map((group) => {
                            const lines = group.criteria.map((requirement) => {
                              const played = cascade.criteria.find(
                                (criterion) => criterion.id === requirement.ref.id,
                              );
                              /*
                              The plancher of the FROZEN version - the content
                              the cascade carries - and not of the criterion as
                              it stands today: this drawer shows « le protocole
                              tel qu'il a été joué » (§12.3), scales included.
                            */
                              const scoring = played?.content.scoring ?? null;
                              const bar = thresholdLabel(
                                requirement.threshold,
                                scoring === null ? null : plancherBound(scoring),
                              );
                              return (
                                <li key={requirement.ref.id}>
                                  {played?.content.name ?? requirement.ref.id}
                                  {bar === null ? '' : ` · ${bar}`}
                                </li>
                              );
                            });
                            // A criterion that stands alone is a line, as before; the
                            // citations of an « l'un de » group (#181) share one frame,
                            // « ou » between them, as the editor draws it.
                            if (!group.either || group.criteria.length < 2) return lines;
                            return (
                              <li
                                key={group.criteria
                                  .map((requirement) => requirement.ref.id)
                                  .join('+')}
                                data-testid={SESSIONS_TEST_IDS.criterionGroup}
                                data-or-group={group.orGroup ?? ''}
                                className="my-0.5 border-gray-300 border-l-2 pl-2.5"
                              >
                                <span className="font-bold text-gray-500 uppercase tracking-wide">
                                  l’un de
                                </span>
                                <ul>
                                  {lines.flatMap((line, rank) =>
                                    rank === 0
                                      ? [line]
                                      : [
                                          <li
                                            // biome-ignore lint/suspicious/noArrayIndexKey: positional separators, one between two neighbouring criteria
                                            key={`or-${rank}`}
                                            aria-hidden="true"
                                            className="font-bold text-gray-500 uppercase tracking-wide"
                                          >
                                            ou
                                          </li>,
                                          line,
                                        ],
                                  )}
                                </ul>
                              </li>
                            );
                          })}
                        </ul>
                        {objective.quantity.minSentences === null &&
                        objective.quantity.minChars === null ? null : (
                          <p className="text-gray-600 text-xs">
                            Quantité attendue :{' '}
                            {[
                              objective.quantity.minSentences === null
                                ? null
                                : `${objective.quantity.minSentences} phrases`,
                              objective.quantity.minChars === null
                                ? null
                                : `${objective.quantity.minChars} caractères`,
                            ]
                              .filter((part): part is string => part !== null)
                              .join(' et ')}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {step.conditions.length === 0 ? null : (
                  <div className="flex flex-col gap-1">
                    <h4 className="font-semibold text-gray-600 text-xs uppercase tracking-wide">
                      Conditions de sortie · la première vraie gagne
                    </h4>
                    <ol className="flex flex-col gap-1 text-sm">
                      {step.conditions.map((condition, index) => (
                        <li key={condition.id} className="text-gray-700">
                          <b>{index + 1}.</b> si {conditionLabel(condition, names)} → alors{' '}
                          {condition.actions
                            .map((action) => actionLabel(action, names))
                            .join(', puis ')}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </section>
            ))}

            <footer className="flex flex-col gap-1.5 border-gray-200 border-t pt-3 text-sm">
              <Link
                to="/admin/protocoles/$protocolId"
                params={{ protocolId: protocol.id }}
                search={{ version: protocol.versionId }}
                className="text-bleu-nuit underline underline-offset-2"
              >
                Ouvrir le protocole en v{protocol.versionNumber}
              </Link>
              {cascade.persona === null ? null : (
                <Link
                  to="/admin/personas/$personaId"
                  params={{ personaId: cascade.persona.id }}
                  search={{ version: cascade.persona.versionId }}
                  className="text-bleu-nuit underline underline-offset-2"
                >
                  Ouvrir {cascade.persona.name} en v{cascade.persona.versionNumber}
                </Link>
              )}
              {cascade.criteria.map((criterion) => (
                <Link
                  key={criterion.id}
                  to="/admin/criteres/$criterionId"
                  params={{ criterionId: criterion.id }}
                  search={{ version: criterion.versionId }}
                  className="text-bleu-nuit underline underline-offset-2"
                >
                  Ouvrir {criterion.content.name} en v{criterion.versionNumber}
                </Link>
              ))}
            </footer>
          </>
        )}
      </dialog>
    </div>
  );
}
